import { useMutation, useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST, queryFnGET } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { SubmitTodoForm } from "features/TodoOploggSidebar/formSchema";
import { Todo } from "features/TodoOploggSidebar/Todo/todoList/todoElement/TodoElement";
import { useCallback, useState } from "react";
import { useDropsRole } from "stores/useDropsRole";
import { useWebsocket } from "websocket/useWebsocket";
import {
  BackendIdentifier,
  TodoMessage,
  WebsocketTopic,
} from "websocket/websocketSharedWorkerTypes";

const TODO_QUERY_KEY = "todo";

const useTodo = () => {
  const [todoNotifications, setTodoNotifications] = useState(0);

  const { mutate: post, status: postStatus } = useMutation({
    mutationFn: (data: SubmitTodoForm) =>
      mutationFnPOST<undefined, SubmitTodoForm>(
        `${getBackendUrl()}/todo`,
        data,
      ),
  });

  const { status, data } = useQuery({
    queryKey: [TODO_QUERY_KEY],
    refetchOnWindowFocus: true,
    queryFn: ({ signal }) =>
      queryFnGET<Todo[]>({
        signal,
        url: `${getBackendUrl()}/todo`,
      }),
  });

  const { role: currentRole } = useDropsRole();

  const handleTodoMessage = useCallback(
    ({ message }: TodoMessage) => {
      switch (message.action) {
        case "ADD": {
          let changed = false;
          queryClient.setQueryData<Todo[]>([TODO_QUERY_KEY], (prevState) => {
            if (!prevState?.find((todo) => todo.id === message.todo.id)) {
              changed = true;
              return prevState ? [...prevState, message.todo] : [message.todo];
            }
            return prevState;
          });

          if (changed && message.todo.role === currentRole) {
            setTodoNotifications((prev) => prev + 1);
          }
          break;
        }

        case "UPDATE": {
          queryClient.setQueryData<Todo[]>([TODO_QUERY_KEY], (prevState) =>
            prevState
              ? [
                  ...prevState.filter((todo) => todo.id !== message.todo.id),
                  message.todo,
                ]
              : [message.todo],
          );
          break;
        }

        case "DELETE":
      }
    },
    [currentRole],
  );

  useWebsocket(BackendIdentifier.DROPS_BACKEND, {
    [WebsocketTopic.TODO]: handleTodoMessage,
  });

  return {
    post,
    postStatus,
    data,
    status,
    todoNotifications, // used alongside oploggnotifications so todo is specified in name
    setTodoNotifications,
  };
};

export default useTodo;
